'use client';

import { useEffect, useId, useLayoutEffect, useRef } from 'react';
import { ArrowLeft, Headphones, Sparkles, UserRound } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Loader } from '@/components/ui/loader';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import type { getPostHogSupportCopy } from '@/i18n/posthog-support-copy';
import { cn } from '@/lib/utils';
import type {
  SupportMessage,
  SupportTicket,
} from '@/lib/posthog-support/types';

import {
  getPostHogSupportContentWidth,
  getPostHogSupportHorizontalPadding,
} from './posthog-support-layout';
import {
  getSupportReplyTextareaHeight,
  getSupportStatusPresentation,
  getTicketDisplayTitle,
} from './posthog-support-state';

type SupportCopy = ReturnType<typeof getPostHogSupportCopy>;

function formatMessageTime(value: string, locale: string): string | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
  }).format(date);
}

function MessageRow({
  copy,
  locale,
  message,
}: {
  copy: SupportCopy;
  locale: string;
  message: SupportMessage;
}) {
  const isCustomer = message.author === 'customer';
  const isAi = message.author === 'ai';
  const authorLabel = isCustomer
    ? copy.you
    : isAi
      ? copy.aiAssistant
      : copy.supportTeam;
  const time = formatMessageTime(message.createdAt, locale);
  const AuthorIcon = isCustomer ? UserRound : isAi ? Sparkles : Headphones;

  return (
    <article
      className={cn(
        'flex gap-2.5',
        isCustomer ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      <span
        className={cn(
          'mt-1 flex size-7 shrink-0 items-center justify-center rounded-full',
          isCustomer
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-muted-foreground'
        )}
      >
        <AuthorIcon className="size-3.5" aria-hidden />
      </span>
      <div
        className={cn(
          'max-w-[82%] min-w-0',
          isCustomer ? 'text-end' : 'text-start'
        )}
      >
        <div
          className={cn(
            'flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs',
            isCustomer ? 'justify-end' : 'justify-start'
          )}
        >
          <span className="font-medium">{authorLabel}</span>
          {time ? (
            <time
              dateTime={message.createdAt}
              className="text-muted-foreground"
            >
              {time}
            </time>
          ) : null}
        </div>
        <p
          className={cn(
            'mt-1 rounded-xl px-3 py-2 text-start text-sm leading-6 text-pretty [overflow-wrap:anywhere] whitespace-pre-wrap',
            isCustomer
              ? 'bg-primary text-primary-foreground rounded-se-sm'
              : 'bg-muted/70 rounded-ss-sm'
          )}
          dir="auto"
        >
          {message.content}
        </p>
      </div>
    </article>
  );
}

export function PostHogSupportTicketView({
  compact,
  copy,
  error,
  isSending,
  locale,
  messages,
  onBack,
  onRetry,
  onReplyChange,
  onSubmitReply,
  reply,
  readError,
  isRetrying,
  ticket,
}: {
  compact: boolean;
  copy: SupportCopy;
  error: string | null;
  isSending: boolean;
  locale: string;
  messages: SupportMessage[];
  onBack: () => void;
  onRetry: () => void | Promise<void>;
  onReplyChange: (reply: string) => void;
  onSubmitReply: () => void | Promise<void>;
  reply: string;
  readError: string | null;
  isRetrying: boolean;
  ticket: SupportTicket | null;
}) {
  const endRef = useRef<HTMLDivElement>(null);
  const replyRef = useRef<HTMLTextAreaElement>(null);
  const inputId = useId();
  const errorId = useId();
  const status = getSupportStatusPresentation(ticket?.status ?? '');

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, ticket?.id]);

  useLayoutEffect(() => {
    const textarea = replyRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${getSupportReplyTextareaHeight(
      textarea.scrollHeight
    )}px`;
    textarea.style.overflowY = textarea.scrollHeight > 128 ? 'auto' : 'hidden';
  }, [reply]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        className={cn(
          'border-border/35 shrink-0 border-b py-2.5',
          getPostHogSupportHorizontalPadding(compact)
        )}
      >
        <div
          className={cn(
            'flex items-center gap-2',
            getPostHogSupportContentWidth(compact)
          )}
        >
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="-ms-2 size-11 rounded-full sm:size-8"
            onClick={onBack}
            aria-label={copy.back}
          >
            <ArrowLeft className="size-4 rtl:rotate-180" aria-hidden="true" />
          </Button>
          <div className="min-w-0 flex-1">
            <h2
              className="truncate text-sm font-semibold"
              data-support-view-heading
              tabIndex={-1}
            >
              {ticket?.title ||
                (ticket ? getTicketDisplayTitle(ticket) : copy.myRequests)}
            </h2>
            <p className="text-muted-foreground mt-0.5 text-xs">
              {copy[status.copyKey]}
            </p>
          </div>
        </div>
      </div>

      <ScrollArea
        className="min-h-0 flex-1"
        viewportClassName="overflow-x-hidden"
        maskHeight={16}
      >
        <div
          className={cn(
            'space-y-4 py-4',
            getPostHogSupportHorizontalPadding(compact),
            getPostHogSupportContentWidth(compact)
          )}
        >
          {messages.length > 0 ? (
            messages.map((message) => (
              <MessageRow
                key={message.id}
                copy={copy}
                locale={locale}
                message={message}
              />
            ))
          ) : readError ? (
            <div className="py-8 text-center">
              <p role="alert" className="text-destructive text-sm text-pretty">
                {readError}
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="mt-3 min-h-11 sm:min-h-8"
                aria-disabled={isRetrying}
                aria-busy={isRetrying}
                onClick={() => {
                  if (isRetrying) return;
                  void onRetry();
                }}
              >
                {isRetrying ? (
                  <span aria-hidden="true">
                    <Loader />
                  </span>
                ) : null}
                {copy.retry}
              </Button>
            </div>
          ) : (
            <p className="text-muted-foreground py-8 text-center text-sm text-pretty">
              {copy.emptyMessages}
            </p>
          )}
          {readError && messages.length > 0 ? (
            <div className="bg-destructive/5 rounded-lg px-3 py-2 text-center">
              <p role="alert" className="text-destructive text-xs text-pretty">
                {readError}
              </p>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="mt-1 min-h-11 sm:min-h-8"
                aria-disabled={isRetrying}
                aria-busy={isRetrying}
                onClick={() => {
                  if (isRetrying) return;
                  void onRetry();
                }}
              >
                {isRetrying ? (
                  <span aria-hidden="true">
                    <Loader />
                  </span>
                ) : null}
                {copy.retry}
              </Button>
            </div>
          ) : null}
          <div ref={endRef} />
        </div>
      </ScrollArea>

      <form
        className={cn(
          'border-border/35 bg-background shrink-0 border-t pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]',
          getPostHogSupportHorizontalPadding(compact)
        )}
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmitReply();
        }}
      >
        <div className={getPostHogSupportContentWidth(compact)}>
          <label htmlFor={inputId} className="sr-only">
            {copy.replyPlaceholder}
          </label>
          <div className="flex items-end gap-2">
            <Textarea
              ref={replyRef}
              id={inputId}
              value={reply}
              onChange={(event) => onReplyChange(event.target.value)}
              placeholder={copy.replyPlaceholder}
              disabled={isSending || !ticket}
              rows={1}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? errorId : undefined}
              className="border-border/35 focus-visible:border-border/55 max-h-32 min-h-11 resize-none overflow-y-hidden py-2 text-base sm:text-sm"
            />
            <Button
              type="submit"
              size="sm"
              disabled={isSending || !ticket || !reply.trim()}
              className="h-11 shrink-0 sm:h-10"
            >
              {isSending ? (
                <>
                  <span aria-hidden="true">
                    <Loader />
                  </span>
                  {copy.sending}
                </>
              ) : (
                copy.sendReply
              )}
            </Button>
          </div>
          {error ? (
            <p
              id={errorId}
              role="alert"
              className="text-destructive mt-2 text-xs text-pretty"
            >
              {error}
            </p>
          ) : null}
        </div>
      </form>
    </div>
  );
}
